import { useCallback } from 'react';

import { useTranslation } from '@lyearn/i18n';
import { useSnackbar } from '@lyearn/ui';
import { DEFAULT_ASSIGNMENT_RESTRICTION } from '@/components/Form/components/AssignmentRestriction/consts';
import { useAppConfig } from '@/hooks/useAppConfig';
import { useUserContext } from '@/modules/auth/hooks/useUser';
import { useCreateSurveyMutation } from '@/modules/corporate/modules/survey/views/SurveyCreator/graphql/mutations/generated/createSurvey';
import { ResourceStatusEnum, ResourceTypeEnum } from '@/types/schema';
import { getRandomCardImage } from '@/utils/cardImage';

interface UseCreateSurveyInBlockOptions {
  onCreated: (surveyId: string) => void;
}

const useCreateSurveyInBlock = ({ onCreated }: UseCreateSurveyInBlockOptions) => {
  const { t } = useTranslation();
  const { showErrorSnackbar } = useSnackbar();
  const user = useUserContext();
  const [{ fetching }, executeCreateSurvey] = useCreateSurveyMutation();
  const { site } = useAppConfig();
  const { defaultContentVisibilitySettings, enableAssignmentRestriction } = site;

  const onCreate = useCallback(
    (name: string) => {
      executeCreateSurvey({
        input: {
          name,
          duration: 5 * 60,
          cover: getRandomCardImage(ResourceTypeEnum.Survey),
          visibilitySettings: defaultContentVisibilitySettings,
          subWorkspaceIds: user.currentSubWorkspace ? [user.currentSubWorkspace] : undefined,
          status: ResourceStatusEnum.Draft,
          maintainDraftState: true,
          assignmentRestriction: enableAssignmentRestriction
            ? DEFAULT_ASSIGNMENT_RESTRICTION
            : undefined,
        },
      }).then((response) => {
        const surveyId = response.data?.createSurvey.survey?.id;
        if (surveyId) {
          onCreated(surveyId);
        } else {
          showErrorSnackbar({ message: t('SurveyEditor:Creator.SurveyCreateError') });
        }
      });
    },
    [
      defaultContentVisibilitySettings,
      enableAssignmentRestriction,
      executeCreateSurvey,
      onCreated,
      showErrorSnackbar,
      t,
      user.currentSubWorkspace,
    ],
  );

  return { creating: fetching, onCreate };
};

export default useCreateSurveyInBlock;
